import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { useDebouncedValue } from '../sales/useAbortableGet';
import { ConfirmDialog } from '../ConfirmDialog';
import { SkuInput } from './SkuInput';
import { SupplierInput } from './SupplierInput';
import { LineStatus, inr } from './PurchaseBits';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LINE_GRID = '200px minmax(140px, 1fr) 75px 110px 65px 105px 90px 110px 210px 36px';
const blankLine = () => ({ key: Math.random().toString(36).slice(2), sku_code: '', product_name: '', qty: '', unit_cost: '', gst_pct: 18 });
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const round2 = n => Math.round((n + Number.EPSILON) * 100) / 100;

const savedUnitCost = l => l.unit_cost ?? (l.amount != null && Number(l.qty) > 0 ? round2(l.amount / l.qty) : '');
const toForm = invoice => invoice
  ? { supplier: invoice.supplier, invoice_no: invoice.invoice_no, invoice_date: invoice.invoice_date, notes: invoice.notes || '', charges: invoice.charges || '',
      lines: invoice.lines.map(l => ({ ...blankLine(), sku_code: l.sku_code, product_name: l.product_name, qty: l.qty ?? '', unit_cost: savedUnitCost(l), gst_pct: l.gst_pct ?? 0 })) }
  : { supplier: '', invoice_no: '', invoice_date: today(), notes: '', charges: '', lines: [blankLine()] };

const isFilled = l => l.sku_code || l.qty || l.unit_cost;
const payloadOf = form => ({ ...form, lines: form.lines.filter(isFilled).map(({ sku_code, qty, unit_cost, gst_pct }) => ({ sku_code, qty, unit_cost, gst_pct })) });

// Amount = unit cost x qty, GST on top; rounded per line, totals are sums of rounded lines.
const lineMoney = line => {
  if (line.qty === '' || line.unit_cost === '' || Number.isNaN(Number(line.qty)) || Number.isNaN(Number(line.unit_cost))) return null;
  const amount = round2(Number(line.qty) * Number(line.unit_cost));
  const gst = round2(amount * Number(line.gst_pct || 0) / 100);
  return { amount, gst, total: round2(amount + gst) };
};

const formTotals = lines => lines.reduce((t, line) => {
  const money = lineMoney(line);
  if (!money) return t;
  return { qty: t.qty + Number(line.qty), amount: round2(t.amount + money.amount), gst: round2(t.gst + money.gst), total: round2(t.total + money.total) };
}, { qty: 0, amount: 0, gst: 0, total: 0 });

const Figure = ({ value, testId }) => (
  <div className="rounded-md bg-gray-50 px-2 py-2 text-right font-mono text-[12px] text-gray-700" data-testid={testId}>{value == null ? '–' : inr(value)}</div>
);

const LineRow = ({ line, index, status, country, onChange, onRemove }) => {
  const money = lineMoney(line);
  return (
    <div className={`grid items-start gap-2 border-b px-3 py-2 ${status?.errors?.length ? 'bg-red-50/50' : ''} ${status && !status.errors?.length ? 'bg-green-50/40' : ''}`} style={{ gridTemplateColumns: LINE_GRID }} data-testid={`purchase-line-${index}`}>
      <SkuInput value={line.sku_code} country={country} index={index} onPick={picked => onChange({ ...line, ...picked })} />
      <div className="truncate pt-2 text-[12px] text-gray-600">{line.product_name || status?.product_name || '–'}</div>
      <Input type="number" min="1" inputMode="numeric" aria-label={`Quantity, line ${index + 1}`} value={line.qty} onChange={e => onChange({ ...line, qty: e.target.value })} className="h-9 text-right text-[12px]" data-testid={`purchase-line-qty-${index}`} />
      <Input type="number" min="0" step="0.01" inputMode="decimal" aria-label={`Unit cost excl. GST, line ${index + 1}`} value={line.unit_cost} onChange={e => onChange({ ...line, unit_cost: e.target.value })} className="h-9 text-right text-[12px]" data-testid={`purchase-line-unit-cost-${index}`} />
      <Input type="number" min="0" max="100" inputMode="decimal" aria-label={`GST %, line ${index + 1}`} value={line.gst_pct} onChange={e => onChange({ ...line, gst_pct: e.target.value })} className="h-9 text-right text-[12px]" data-testid={`purchase-line-gst-${index}`} />
      <Figure value={money?.amount} testId={`purchase-line-amount-${index}`} />
      <Figure value={money?.gst} testId={`purchase-line-gst-amount-${index}`} />
      <Figure value={money?.total} testId={`purchase-line-total-${index}`} />
      <LineStatus line={status} index={index} />
      <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={onRemove} aria-label={`Remove line ${index + 1}`} title="Remove line" data-testid={`purchase-line-remove-${index}`}><Trash2 className="h-4 w-4 text-red-600" aria-hidden="true" /></Button>
    </div>
  );
};

const HEADINGS = [['SKU', ''], ['Product', ''], ['Qty', 'text-right'], ['Unit cost excl. GST', 'text-right'], ['GST %', 'text-right'],
  ['Amount excl. GST', 'text-right'], ['GST', 'text-right'], ['Total incl. GST', 'text-right'], ['Check', ''], ['', '']];

export const PurchaseFormDialog = ({ open, onOpenChange, invoice, country, isAdmin, onSaved, onOpenExisting }) => {
  const [form, setForm] = useState(toForm(invoice));
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [recheck, setRecheck] = useState(0);
  const [attempted, setAttempted] = useState(false);
  const [postConfirmOpen, setPostConfirmOpen] = useState(false);
  const [pendingClose, setPendingClose] = useState(null); // action to run once the user agrees to discard unsaved lines
  const baseline = useRef('');
  const debouncedForm = useDebouncedValue(form, 500);

  useEffect(() => {
    if (!open) return;
    const initial = toForm(invoice);
    baseline.current = JSON.stringify(payloadOf(initial));
    setForm(initial); setCheck(null); setAttempted(false); setPostConfirmOpen(false); setPendingClose(null);
  }, [open, invoice]);

  // Unsaved = anything differs from what the dialog opened with (a blank form, or the saved draft).
  const dirty = open && JSON.stringify(payloadOf(form)) !== baseline.current;

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = event => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  // Escape, an outside click, the X, Cancel and "Open existing" all come through here.
  const requestClose = then => {
    if (busy) return;
    if (dirty) setPendingClose(() => then); else then();
  };
  const close = () => requestClose(() => onOpenChange(false));
  const openExisting = id => requestClose(() => onOpenExisting(id));
  // A toast can outlive the form: read the latest state when it is clicked, not the state at Save time.
  const latest = useRef({});
  latest.current = { open, openExisting, onOpenExisting };
  const openExistingFromToast = id => { const l = latest.current; if (l.open) l.openExisting(id); else l.onOpenExisting(id); };

  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    axios.post(`${API}/purchases/check`, payloadOf(debouncedForm), { params: { country, ...(invoice?.id && { exclude_id: invoice.id }) }, signal: controller.signal })
      .then(res => setCheck(res.data)).catch(() => {});
    return () => controller.abort();
  }, [debouncedForm, open, country, invoice, recheck]);

  const setLine = (index, line) => setForm(f => ({ ...f, lines: f.lines.map((l, i) => (i === index ? line : l)) }));
  const removeLine = index => setForm(f => ({ ...f, lines: f.lines.length > 1 ? f.lines.filter((_, i) => i !== index) : [blankLine()] }));

  const save = async post => {
    setAttempted(true);
    setBusy(true);
    try {
      const params = { country, ...(post && { post: true }) };
      const res = invoice?.id
        ? await axios.put(`${API}/purchases/${invoice.id}`, payloadOf(form), { params })
        : await axios.post(`${API}/purchases`, payloadOf(form), { params });
      toast.success(post ? `Invoice ${res.data.invoice_no} posted: stock and cost updated` : `Draft ${res.data.invoice_no} saved`);
      baseline.current = JSON.stringify(payloadOf(form));
      onSaved(res.data);
      return true;
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.existing_id) {
        toast.error(detail.message, { action: { label: 'Open existing', onClick: () => openExistingFromToast(detail.existing_id) } });
      } else {
        toast.error(typeof detail === 'string' ? detail : detail?.message || 'Could not save invoice');
        if (detail?.check) setCheck(detail.check);
      }
      return false;
    } finally {
      setBusy(false);
    }
  };

  // Required-field messages only after Save/Post; a duplicate invoice is flagged straight away.
  const problems = attempted ? check?.problems || [] : (check?.duplicate ? check.problems.filter(p => p.includes('already exists')) : []);
  const totals = formTotals(form.lines);
  const validCheckLines = (check?.lines || []).filter(l => !l.errors?.length);
  const costChanges = new Set(validCheckLines.filter(l => l.new_cost != null && Number(l.new_cost) !== Number(l.current_cost)).map(l => l.sku_code)).size;
  // Bundle SKUs add qty x bundle size of the base SKU, so use the backend's stock_qty when the check has run.
  const unitsAdded = check?.lines ? validCheckLines.reduce((s, l) => s + (Number(l.stock_qty) || 0), 0) : totals.qty;
  const filledLines = form.lines.filter(isFilled).length;
  const confirmPost = async () => { if (!(await save(true))) throw new Error('post failed'); };
  const discard = () => { const then = pendingClose; baseline.current = JSON.stringify(payloadOf(form)); setPendingClose(null); then?.(); };
  return (
    <Dialog open={open} onOpenChange={value => (value ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[92vh] max-w-[1240px] overflow-y-auto rounded-2xl" data-testid="purchase-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-[17px] font-semibold">{invoice?.id ? `Edit draft ${invoice.invoice_no}` : 'New purchase'}</DialogTitle>
          <DialogDescription>Enter unit cost × qty per line; GST is added on top. Posting adds stock and sets each SKU&apos;s cost to the unit cost excl. GST, plus its share of any freight &amp; other charges.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div className="md:col-span-2"><Label className="text-[12px]">Supplier *</Label><SupplierInput value={form.supplier} country={country} onChange={supplier => setForm(f => ({ ...f, supplier }))} /></div>
          <div><Label className="text-[12px]">Invoice no. *</Label><Input value={form.invoice_no} onChange={e => setForm({ ...form, invoice_no: e.target.value })} data-testid="purchase-invoice-no-input" /></div>
          <div><Label className="text-[12px]">Invoice date *</Label><Input type="date" value={form.invoice_date} onChange={e => setForm({ ...form, invoice_date: e.target.value })} data-testid="purchase-invoice-date-input" /></div>
          <div className="md:col-span-2">
            <Label className="text-[12px]">Freight &amp; other charges, excl. GST (₹)</Label>
            <Input type="number" min="0" step="0.01" inputMode="decimal" value={form.charges} placeholder="0"
              onChange={e => setForm({ ...form, charges: e.target.value })} data-testid="purchase-charges-input" />
            <p className="mt-1 text-[11px] text-gray-500">Freight, customs, loading etc. Spread over the lines by value and added to each SKU&apos;s cost (landed cost).</p>
          </div>
        </div>
        {problems.length > 0 && (
          <div className="rounded-lg bg-red-50 px-3 py-2 text-[12px] text-red-700" data-testid="purchase-header-problems">
            {problems.join(' · ')}
            {check?.duplicate && <Button size="sm" variant="link" className="h-auto px-2 text-[12px]" onClick={() => openExisting(check.duplicate.id)} data-testid="purchase-open-existing">Open existing invoice</Button>}
          </div>
        )}
        <div className="overflow-x-auto rounded-xl border">
          <div className="min-w-[1180px]">
            <div className="grid gap-2 bg-gray-50 px-3 py-2 text-[11px] font-semibold uppercase text-gray-500" style={{ gridTemplateColumns: LINE_GRID }}>
              {HEADINGS.map(([label, align], i) => <span key={`h-${i}`} className={align}>{label}</span>)}
            </div>
            {form.lines.map((line, index) => (
              <LineRow key={line.key} line={line} index={index} status={isFilled(line) ? check?.lines?.[form.lines.slice(0, index).filter(isFilled).length] : null} country={country} onChange={l => setLine(index, l)} onRemove={() => removeLine(index)} />
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" variant="outline" size="sm" onClick={() => setForm(f => ({ ...f, lines: [...f.lines, blankLine()] }))} data-testid="purchase-add-line"><Plus className="mr-1 h-4 w-4" />Add line</Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => setRecheck(n => n + 1)} data-testid="purchase-recheck"><RefreshCw className="mr-1 h-4 w-4" />Re-check</Button>
          <div className="ml-auto flex flex-wrap gap-5 text-[13px]" data-testid="purchase-running-total">
            <span>{check?.totals?.problem_lines ?? 0} line(s) with problems</span>
            <span>Qty <b data-testid="purchase-total-qty-value">{totals.qty}</b></span>
            <span>Amount excl. GST <b data-testid="purchase-total-amount-value">{inr(totals.amount)}</b></span>
            <span>GST <b data-testid="purchase-total-gst-value">{inr(totals.gst)}</b></span>
            {Number(form.charges) > 0 && <span>Freight &amp; charges <b data-testid="purchase-total-charges-value">{inr(Number(form.charges))}</b></span>}
            <span className="text-[14px]">Invoice total incl. GST <b data-testid="purchase-total-incl-gst-value">{inr(totals.total + (Number(form.charges) > 0 ? Number(form.charges) : 0))}</b></span>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-4">
          {!isAdmin && <p className="mr-auto text-[12px] text-gray-600" data-testid="purchase-form-admin-hint">Save as draft; only an admin can post it to stock.</p>}
          <Button variant="outline" onClick={close} data-testid="purchase-cancel">Cancel</Button>
          <Button variant="outline" disabled={busy} onClick={() => save(false)} data-testid="purchase-save-draft">Save as draft</Button>
          {isAdmin && (
            <Button disabled={busy || check?.totals?.problem_lines > 0 || (attempted && !check?.ok)} onClick={() => setPostConfirmOpen(true)} className="bg-[#007AFF] text-white hover:bg-[#0066CC]" data-testid="purchase-post">Post invoice</Button>
          )}
        </div>
        <ConfirmDialog
          open={postConfirmOpen}
          onOpenChange={setPostConfirmOpen}
          title={form.invoice_no ? `Post invoice ${form.invoice_no}?` : 'Post this invoice?'}
          description="Posting adds these units to warehouse stock and sets the latest purchase cost for each SKU. To undo it later you will need to reverse the invoice."
          confirmLabel="Post invoice"
          busyLabel="Posting…"
          onConfirm={confirmPost}
          testId="purchase-form-post-confirm-dialog"
        >
          <div className="rounded-lg bg-gray-50 p-3 text-[13px]" data-testid="purchase-form-post-confirm-summary">
            <p>{filledLines} {filledLines === 1 ? 'line' : 'lines'} · <b>{Number(unitsAdded || 0).toLocaleString('en-IN')}</b> units added to stock</p>
            <p className="mt-1">Purchase cost changes on <b>{costChanges}</b> {costChanges === 1 ? 'SKU' : 'SKUs'}</p>
            <p className="mt-1">Value excl. GST <b>{inr(totals.amount)}</b> · incl. GST <b>{inr(totals.total)}</b></p>
          </div>
        </ConfirmDialog>
        <ConfirmDialog
          open={Boolean(pendingClose)}
          onOpenChange={value => { if (!value) setPendingClose(null); }}
          title="Discard this invoice?"
          description={filledLines
            ? `You have unsaved changes on ${filledLines} ${filledLines === 1 ? 'line' : 'lines'}. Close without saving, or keep editing and use Save as draft.`
            : 'You have unsaved changes. Close without saving, or keep editing and use Save as draft.'}
          confirmLabel="Discard changes"
          cancelLabel="Keep editing"
          destructive
          onConfirm={discard}
          testId="purchase-discard-confirm-dialog"
        />
      </DialogContent>
    </Dialog>
  );
};
